import React, { useState, useEffect, useCallback } from "react";
import { Login } from "./Login";
import { ProvedorDados, useDados } from "./dados";
import { auth, sessaoSalva, definirSessao, observarConexao, temPendencias, ErroApi, BASE } from "./api";
import { observarFotos, fotosPendentes } from "./fotos";
import { ProtecaoErro } from "./ui/protecao";
import { MarcaEixo } from "./ui/marca";
import {
  CSS, NAV, Painel, Clientes, Orcamentos, Checklist, OrdensServico, Estoque,
  Financeiro, Notas, Config, Notificacoes, Toasts, Btn, Vazio, uid,
} from "./telas";
import {
  LogOut, Menu, Plus, Loader2, CloudOff, RefreshCw, AlertTriangle,
} from "lucide-react";

/**
 * SHELL
 *
 * O que o protótipo fazia em `useState` e o que era simulação vira real aqui:
 * sessão com token, dados do servidor, aviso de rede caída.
 */

const TITULOS: Record<string, [string, string]> = {
  painel: ["Painel", "Como está a oficina hoje"],
  clientes: ["Clientes", "Cadastro, veículos e histórico"],
  orcamentos: ["Orçamentos", "Passe o preço sem precisar do carro no pátio"],
  recepcao: ["Recepção e checklist", "Documente o veículo antes de encostar a chave"],
  os: ["Ordens de serviço", "Do orçamento à entrega"],
  estoque: ["Estoque", "Peças, entradas, saídas e reposição"],
  financeiro: ["Financeiro", "Contas a receber, a pagar e caixa"],
  notas: ["Notas fiscais", "NFS-e de serviços e NF-e de peças"],
  config: ["Configurações", "Oficina, fiscal, equipe e integrações"],
};

export default function App() {
  const [sessao, setSessao] = useState<any>(null);
  const [conferindo, setConferindo] = useState(true);

  /**
   * Reidrata a sessão ao abrir.
   *
   * O refresh está em cookie httpOnly, então uma chamada basta para saber se
   * ainda vale. Sem isso, recarregar a página derruba o mecânico para a tela
   * de login — no meio do serviço, com a mão suja.
   */
  useEffect(() => {
    (async () => {
      const salva = sessaoSalva();
      if (!salva) { setConferindo(false); return; }
      try {
        const r = await fetch(`${BASE}/auth/renovar`,
          { method: "POST", credentials: "include" });
        if (!r.ok) throw new Error();
        const d = await r.json();
        const s = { ...salva, tokenAcesso: d.tokenAcesso, expiraEm: d.expiraEm };
        definirSessao(s as any);
        setSessao(s);
      } catch { /* sessão expirada: mostra o login */ }
      setConferindo(false);
    })();
  }, []);

  if (conferindo) {
    return (
      <>
        <style>{CSS}</style>
        <div className="of min-h-screen flex items-center justify-center" style={{ background: "var(--ink)" }}>
          <Loader2 size={26} color="#fff" className="animate-spin" />
        </div>
      </>
    );
  }

  if (!sessao) {
    return (<><style>{CSS}</style><Login aoEntrar={setSessao} /></>);
  }

  return (
    <>
      <style>{CSS}</style>
      <ProvedorDados usuario={sessao.usuario} oficinas={sessao.oficinas}>
        <Interior sessao={sessao} sair={() => { auth.sair(); setSessao(null); }} />
      </ProvedorDados>
    </>
  );
}

function Interior({ sessao, sair }: any) {
  const { db, set, carregando, erro, recarregar, acoes } = useDados();
  const [tela, setTela] = useState("painel");
  const [menu, setMenu] = useState(false);
  const [aberta, setAberta] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<any>(null);
  const [toasts, setToasts] = useState<any[]>([]);
  const [pendencias, setPendencias] = useState(0);

  const papel = sessao.oficinas?.[0]?.papel ?? "admin";
  const usuario = {
    id: sessao.usuario.id, nome: sessao.usuario.nome, papel,
    cargo: papel === "proprietario" ? "Proprietário" : papel,
    email: sessao.usuario.email,
    inic: sessao.usuario.nome.split(" ").slice(0, 2).map((p: string) => p[0]).join(""),
  };

  const toast = useCallback((titulo: string, msg?: string, tone = "ok") => {
    const id = uid("T");
    setToasts((t) => [...t, { id, titulo, msg, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5200);
  }, []);

  /**
   * Sincroniza a fila quando a rede volta.
   *
   * O mecânico não vai clicar em "tentar de novo" — ele já saiu da tela. Se o
   * envio não acontecer sozinho, o trabalho dele fica preso no aparelho.
   */
  useEffect(() => {
    const pararTexto = observarConexao((r) =>
      toast("Sincronizado", `${r.enviadas} alteração(ões) que estavam guardadas foram enviadas.`));
    // Foto tem fila própria: não cabe no localStorage e o envio é mais lento,
    // então sobe uma por vez em segundo plano.
    const pararFotos = observarFotos((r) =>
      toast("Fotos enviadas", `${r.enviadas} foto(s) do checklist subiram.`));

    const t = setInterval(async () => {
      const fotos = await fotosPendentes().catch(() => []);
      setPendencias(temPendencias() + fotos.length);
    }, 4000);

    return () => { pararTexto(); pararFotos(); clearInterval(t); };
  }, [toast]);

  /**
   * Ponte entre as telas e o servidor.
   *
   * As telas do protótipo chamam `set(...)` direto. Aqui o `set` continua
   * existindo para a resposta imediata, e as ações que precisam de servidor
   * são interceptadas — assim as telas não precisaram ser reescritas uma a uma.
   */
  const comErro = (fn: () => Promise<any>, sucesso?: (r: any) => void) => async () => {
    try {
      const r = await fn();
      sucesso?.(r);
    } catch (e: any) {
      // A mensagem já vem em português do domínio: "Não dá para aprovar sem
      // essas peças — Pastilha: disponível 1, precisa 2".
      toast("Não deu certo", e instanceof ErroApi ? e.message : "Sem conexão com o servidor.", "erro");
    }
  };

  const itens = NAV.filter((n: any) => n.papeis.includes(papel === "proprietario" ? "admin" : papel));
  const ir = (t: string) => { setTela(t); setAberta(null); setMenu(false); };
  const abrirOS = (id: string) => { setAberta(id); setTela("os"); };
  const novaOS = (cliId: string, veiId?: string, ckId?: string) => {
    setRascunho({ cliId, veiId, ckId }); setTela("os");
  };

  if (carregando && db.clientes.length === 0) {
    return (
      <div className="of min-h-screen flex flex-col items-center justify-center gap-3">
        <Loader2 size={24} className="t-mut animate-spin" />
        <div className="text-sm t-mut">Carregando os dados da oficina…</div>
      </div>
    );
  }

  if (erro && db.clientes.length === 0) {
    return (
      <div className="of min-h-screen flex items-center justify-center p-6">
        <div className="card p-6 text-center" style={{ maxWidth: 400 }}>
          <AlertTriangle size={26} className="t-crit mx-auto mb-3" />
          <div className="dsp mb-1" style={{ fontSize: 17 }}>Não consegui carregar</div>
          <div className="text-sm t-mut mb-4">{erro}</div>
          <Btn variant="p" icon={RefreshCw} onClick={() => recarregar()}>Tentar de novo</Btn>
        </div>
      </div>
    );
  }

  const [titulo, sub] = TITULOS[tela] ?? ["", ""];

  return (
    <div className="of flex min-h-screen">
      <div className="of-veu" data-menu={menu} onClick={() => setMenu(false)} />

      {/* Layout em `.of-lado`, não em style inline: inline vence media query,
          e era por isso que a barra continuava reservando 232px no celular. */}
      <aside className="of-lado" data-menu={menu}>
        <div className="px-4 py-4 flex items-center gap-2.5" style={{ borderBottom: "1px solid var(--ink3)" }}>
          <MarcaEixo tamanho={32} />
          <div>
            <div className="dsp" style={{ color: "#fff", fontSize: 16, lineHeight: 1 }}>Eixo Auto Control</div>
            <div className="eyebrow" style={{ color: "var(--gulf)", fontSize: 9.5, marginTop: 2 }}>
              {sessao.oficinas?.[0]?.nome ?? "Gestão de oficina"}
            </div>
          </div>
        </div>

        <nav className="flex-1 py-3 scroll" style={{ overflowY: "auto" }}>
          {itens.map((n: any) => (
            <button key={n.k} className={`nav-i ${tela === n.k ? "on" : ""}`} onClick={() => ir(n.k)}>
              <n.i size={16} strokeWidth={2.1} />{n.l}<span className="nav-dot" />
            </button>
          ))}
        </nav>

        <div className="p-3" style={{ borderTop: "1px solid var(--ink3)" }}>
          <div className="flex items-center gap-2.5 mb-2.5">
            <span className="mono flex items-center justify-center flex-none"
              style={{ width: 32, height: 32, borderRadius: 6, background: "var(--gulf-d)", color: "#fff", fontSize: 12, fontWeight: 600 }}>
              {usuario.inic}
            </span>
            <div className="flex-1" style={{ minWidth: 0 }}>
              <div className="text-sm font-semibold truncate" style={{ color: "#fff" }}>{usuario.nome}</div>
              <div className="text-xs truncate" style={{ color: "#8D9AA6" }}>{usuario.cargo}</div>
            </div>
          </div>
          <button className="nav-i" style={{ paddingLeft: 11, borderRadius: 5 }} onClick={sair}>
            <LogOut size={15} strokeWidth={2.1} />Sair
          </button>
        </div>
      </aside>

      <div className="flex-1" style={{ minWidth: 0 }}>
        <header className="flex flex-wrap items-center gap-3 px-5 py-3.5"
          style={{ background: "var(--card)", borderBottom: "1px solid var(--line)", position: "sticky", top: 0, zIndex: 40 }}>
          <button className="btn btn-g btn-xs btn-menu" onClick={() => setMenu(true)} aria-label="Abrir menu">
            <Menu size={17} />
          </button>
          <div className="flex-1" style={{ minWidth: 140 }}>
            <h1 className="dsp" style={{ fontSize: 19 }}>{titulo}</h1>
            <div className="text-xs t-mut">{sub}</div>
          </div>

          {/* Rede caída não pode ser silenciosa: o mecânico precisa saber que
              o trabalho dele está guardado e ainda não subiu. */}
          {pendencias > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5"
              style={{ background: "var(--signal-l)", border: "1px solid #F6D3B6", borderRadius: "var(--r)", color: "var(--signal)" }}>
              <CloudOff size={14} />
              <span className="mono text-xs font-semibold">{pendencias}</span>
              <span className="text-xs">a enviar</span>
            </div>
          )}

          {carregando && <Loader2 size={15} className="t-mut2 animate-spin" />}

          {papel !== "mecanico" && (
            <Btn variant="s" size="xs" icon={Plus} onClick={() => {
              // Sem cliente não há OS. Passar undefined levava o usuário até a
              // tela de OS para ler "Sem veículo" — mensagem certa para outro
              // problema. Melhor dizer o que falta e onde resolver.
              if (!db.clientes[0]) {
                toast("Nenhum cliente cadastrado",
                  "Cadastre o cliente e o veículo dele em Clientes; a OS abre a partir de lá.", "erro");
                setTela("clientes");
                return;
              }
              novaOS(db.clientes[0].id);
            }}>Nova OS</Btn>
          )}
          <ProtecaoErro silencioso>
            <Notificacoes db={db} usuario={usuario} ir={ir} abrirOS={abrirOS} />
          </ProtecaoErro>
        </header>

        {/* `key` na tela: trocar de aba pelo menu zera o erro, e a aba nova
            monta do zero em vez de herdar o aviso da que quebrou. */}
        <main className="p-5 scroll" style={{ maxWidth: 1360 }}>
          <ProtecaoErro key={tela}>
            {tela === "painel" && <Painel db={db} ir={ir} usuario={usuario} abrirOS={abrirOS} />}
            {tela === "clientes" && (
              <Clientes db={db} set={set} toast={toast} abrirOS={abrirOS} novaOS={novaOS} acoes={acoes} />
            )}
            {tela === "orcamentos" && (
              <Orcamentos db={db} set={set} toast={toast} usuario={usuario} abrirOS={abrirOS} acoes={acoes} />
            )}
            {tela === "recepcao" && (
              <Checklist db={db} set={set} toast={toast} usuario={usuario} novaOS={novaOS} acoes={acoes} />
            )}
            {tela === "os" && (
              <OrdensServico db={db} set={set} toast={toast} usuario={usuario}
                aberta={aberta} setAberta={setAberta} rascunho={rascunho} setRascunho={setRascunho}
                emitirNF={(os: any, tipo: any) =>
                  comErro(() => acoes.emitirNota(os._id ?? os.id, tipo),
                    (r) => toast(r.status === "bloqueada" ? "Não dá para emitir" : "Nota enviada",
                      r.status === "bloqueada"
                        ? r.problemas.map((p: any) => p.mensagem).join(" · ")
                        : "Assim que o fisco autorizar, ela aparece aqui.",
                      r.status === "bloqueada" ? "erro" : "ok"))()}
                acoes={acoes} />
            )}
            {tela === "estoque" && <Estoque db={db} set={set} toast={toast} usuario={usuario} acoes={acoes} />}
            {tela === "financeiro" && <Financeiro db={db} set={set} toast={toast} acoes={acoes} />}
            {tela === "notas" && (
              <Notas db={db} toast={toast} abrirOS={abrirOS}
                emitirNF={(os: any, tipo: any) => comErro(() => acoes.emitirNota(os._id ?? os.id, tipo))()} />
            )}
            {tela === "config" && <Config db={db} set={set} toast={toast} />}
          </ProtecaoErro>
        </main>
      </div>

      <Toasts list={toasts} drop={(id: string) => setToasts((t) => t.filter((x) => x.id !== id))} />
    </div>
  );
}
